import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { transformForPDF } from './transform';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica'
  },
  header: {
    marginBottom: 20,
    textAlign: 'center'
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10
  },
  subtitle: {
    fontSize: 12,
    color: '#666666'
  },
  section: {
    marginBottom: 15
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    backgroundColor: '#f0f0f0',
    padding: 5
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    width: '30%'
  },
  value: {
    fontSize: 10,
    width: '70%'
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000000'
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row'
  },
  tableCol: {
    width: '16.66%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000000'
  },
  tableCell: {
    margin: 'auto',
    marginTop: 5,
    fontSize: 8,
    padding: 2
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold'
  },
  footer: {
    marginTop: 30,
    borderTop: '1pt solid #000000',
    paddingTop: 10
  },
  signature: {
    marginTop: 20,
    borderBottom: '1pt solid #000000',
    paddingBottom: 5,
    width: '50%'
  }
});

const ElectricalPDF = ({ formData }) => {
  const pdfData = transformForPDF(formData);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{pdfData.title}</Text>
          <Text style={styles.subtitle}>Certificate Number: {pdfData.certification.certificateNumber}</Text>
          <Text style={styles.subtitle}>Issue Date: {pdfData.certification.issueDate}</Text>
        </View>

        {/* Property Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Property Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={styles.value}>{pdfData.property.address}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Landlord/Agent:</Text>
            <Text style={styles.value}>{pdfData.property.landlord}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Tenant:</Text>
            <Text style={styles.value}>{pdfData.property.tenant}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Inspection Date:</Text>
            <Text style={styles.value}>{pdfData.property.inspectionDate}</Text>
          </View>
        </View>

        {/* Engineer Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Engineer Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{pdfData.engineer.name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Electrical Reg No:</Text>
            <Text style={styles.value}>{pdfData.engineer.electricalRegNumber}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Company:</Text>
            <Text style={styles.value}>{pdfData.engineer.company}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Phone:</Text>
            <Text style={styles.value}>{pdfData.engineer.phone}</Text>
          </View>
        </View>

        {/* Circuits Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Electrical Circuits Tested</Text>
          <View style={styles.table}>
            {/* Header */}
            <View style={[styles.tableRow, styles.tableHeader]}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Type</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Location</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Rating</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Device</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Test Result</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Status</Text>
              </View>
            </View>
            {/* Data Rows */}
            {pdfData.circuits.map((circuit, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{circuit.circuitType}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{circuit.location}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{circuit.rating}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{circuit.protectiveDevice}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{circuit.testResult}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={[styles.tableCell, {
                    color: circuit.status === 'PASS' ? 'green' : 'red',
                    fontWeight: 'bold'
                  }]}>
                    {circuit.status}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Remedial Actions */}
        {pdfData.circuits.some(circuit => circuit.remedialAction) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Remedial Actions Required</Text>
            {pdfData.circuits.map((circuit, index) =>
              circuit.remedialAction ? (
                <View key={index} style={styles.row}>
                  <Text style={styles.label}>{circuit.circuitType} ({circuit.location}):</Text>
                  <Text style={styles.value}>{circuit.remedialAction}</Text>
                </View>
              ) : null
            )}
          </View>
        )}

        {/* Additional Notes */}
        {pdfData.certification.additionalNotes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Additional Notes</Text>
            <Text style={styles.value}>{pdfData.certification.additionalNotes}</Text>
          </View>
        )}

        {/* Certification */}
        <View style={styles.footer}>
          <View style={styles.row}>
            <Text style={styles.label}>Next Inspection Due:</Text>
            <Text style={styles.value}>{pdfData.certification.nextInspectionDate}</Text>
          </View>
          <View style={styles.signature}>
            <Text style={styles.label}>Engineer Signature:</Text>
            <Text style={styles.value}>{pdfData.certification.engineerSignature}</Text>
          </View>
          <Text style={[styles.subtitle, { marginTop: 10 }]}>
            This certificate is valid for 12 months from the inspection date
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ElectricalPDF;